<script setup lang="ts">
import { useId, useSlots } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Visible panel title and the landmark's accessible name. */
    label: string
    /** The shared left edge: `compact` 12px, for a conversation · `sheet` 24px, for plain sections sitting on the dock sheet; the body pads itself to match. */
    edge?: 'compact' | 'sheet'
  }>(),
  { edge: 'compact' },
)

defineSlots<{
  /** The panel body. */
  default(): unknown
  /** Controls at the end of the header row. */
  'header-actions'?: () => unknown
  /** Actions or a composer under the body; on a `sheet` edge a lone `ghost` AppButton takes `flush="start"` so its label lines up with the body. */
  footer?: () => unknown
}>()

const slots = useSlots()

const EDGE_CLASSES = {
  compact: { header: 'px-6 py-2', footer: 'px-6 py-4' },
  sheet: { header: 'py-4 pr-8 pl-12', footer: 'px-12 pt-4 pb-12' },
} as const

const headingId = useId()
</script>

<template>
  <aside :aria-labelledby="headingId" class="flex h-full flex-col overflow-hidden">
    <header
      class="flex shrink-0 items-center gap-4 border-b border-border"
      :class="EDGE_CLASSES[props.edge].header"
    >
      <h2 :id="headingId" class="min-w-0 truncate text-body-sm-medium text-text-primary">
        {{ label }}
      </h2>
      <div class="ml-auto flex items-center gap-2">
        <slot name="header-actions" />
      </div>
    </header>

    <div class="flex min-h-0 flex-1 flex-col">
      <slot />
    </div>

    <footer
      v-if="edge === 'compact' || slots.footer"
      class="flex shrink-0 flex-col gap-4"
      :class="EDGE_CLASSES[props.edge].footer"
    >
      <slot name="footer" />
    </footer>
  </aside>
</template>
